import { useCallback, useEffect, type RefObject } from 'react';
import { useDrawingStore } from '@/features/drawing/model';

interface UseCanvasEventsProps {
  containerRef: RefObject<HTMLDivElement | null>;
  screenToCanvas: (
    clientX: number,
    clientY: number
  ) => { x: number; y: number };
  repositionCanvasIfNeeded: (point: { x: number; y: number }) => void;
}

export function useCanvasEvents({
  containerRef,
  screenToCanvas,
  repositionCanvasIfNeeded,
}: UseCanvasEventsProps) {
  const {
    tool,
    drawing,
    modifiers,
    cloneSettings,
    startDrawing,
    continueDrawing,
    continuePanning,
    endDrawing,
    handleKeyDown,
    handleKeyUp,
    setCloneSource,
    createTextBlock,
    setActiveTextBlock,
  } = useDrawingStore();

  // Mouse down handler
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const screenPoint = {
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top,
      };

      // Middle mouse button for panning
      if (e.button === 1) {
        e.preventDefault();
        startDrawing(screenPoint, true);
        return;
      }

      // Space pressed - start panning
      if (modifiers.spacePressed) {
        startDrawing(screenPoint, true);
        return;
      }

      const canvasPoint = screenToCanvas(e.clientX, e.clientY);

      // Text tool - toggle between create and deactivate
      if (tool === 'text') {
        const currentTextBlocks = useDrawingStore.getState().textBlocks;

        if (currentTextBlocks.activeBlockId !== null) {
          e.stopPropagation();
          setActiveTextBlock(null);
          return;
        }

        // Only allow text creation within image bounds
        const { width, height } = useDrawingStore.getState().canvas;
        if (
          canvasPoint.x < 0 ||
          canvasPoint.y < 0 ||
          canvasPoint.x > width ||
          canvasPoint.y > height
        ) {
          return;
        }

        e.stopPropagation();
        createTextBlock(canvasPoint);
        repositionCanvasIfNeeded(canvasPoint);
        return;
      }

      // Alt+Click or Cmd+Click to set clone source
      if ((e.altKey || e.metaKey) && tool === 'clone') {
        setCloneSource(canvasPoint);
        return;
      }

      // For clone tool, calculate offset if needed
      if (tool === 'clone' && cloneSettings.source && !cloneSettings.offset) {
        const offset = {
          x: cloneSettings.source.x - canvasPoint.x,
          y: cloneSettings.source.y - canvasPoint.y,
        };
        useDrawingStore.setState((state) => ({
          cloneSettings: {
            ...state.cloneSettings,
            offset: offset,
          },
        }));
      }

      startDrawing(canvasPoint);

      // Immediately draw a point on click without moving
      if (!modifiers.spacePressed && !drawing.isPanning) {
        continueDrawing(canvasPoint);
      }
    },
    [
      tool,
      cloneSettings.source,
      cloneSettings.offset,
      setCloneSource,
      startDrawing,
      continueDrawing,
      drawing.isPanning,
      modifiers.spacePressed,
      screenToCanvas,
      createTextBlock,
      repositionCanvasIfNeeded,
      setActiveTextBlock,
      containerRef,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const screenPoint = {
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top,
      };

      if (drawing.isPanning) {
        continuePanning(screenPoint);
      } else if (drawing.isDrawing) {
        const canvasPoint = screenToCanvas(e.clientX, e.clientY);
        continueDrawing(canvasPoint);
      }
    },
    [
      continuePanning,
      continueDrawing,
      drawing.isDrawing,
      drawing.isPanning,
      screenToCanvas,
      containerRef,
    ]
  );

  const handleMouseUp = useCallback(() => {
    endDrawing();
  }, [endDrawing]);

  // Global mouse events for drawing/panning
  useEffect(() => {
    if (drawing.isDrawing || drawing.isPanning) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [drawing.isDrawing, drawing.isPanning, handleMouseMove, handleMouseUp]);

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDownWrapper = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditingText =
        target.contentEditable === 'true' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA';

      if (isEditingText) return;

      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
      }
      if (e.key === ' ') {
        e.preventDefault();
      }
      handleKeyDown(e);
    };

    const handleKeyUpWrapper = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditingText =
        target.contentEditable === 'true' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA';

      if (isEditingText) return;

      if (e.key === ' ') {
        e.preventDefault();
      }
      handleKeyUp(e);
    };

    // When the window loses focus (DevTools shortcut, alt-tab, OS popup), the
    // keyup for any held modifier is delivered to the new focus owner and never
    // reaches us. Without this reset, Shift can stay "stuck" and the pencil
    // keeps drawing straight lines until the user manually presses + releases
    // Shift again.
    const handleBlur = () => {
      const { setModifier } = useDrawingStore.getState();
      setModifier('shiftPressed', false);
      setModifier('altPressed', false);
      setModifier('ctrlPressed', false);
      setModifier('spacePressed', false);
    };

    window.addEventListener('keydown', handleKeyDownWrapper);
    window.addEventListener('keyup', handleKeyUpWrapper);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDownWrapper);
      window.removeEventListener('keyup', handleKeyUpWrapper);
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Get cursor style
  const getCursorStyle = useCallback(() => {
    if (modifiers.spacePressed || drawing.isPanning) {
      return drawing.isPanning ? 'grabbing' : 'grab';
    }
    if (tool === 'text') {
      return 'text';
    }
    if (tool === 'pencil' || tool === 'eraser' || tool === 'clone') {
      return 'none';
    }
    return 'crosshair';
  }, [modifiers.spacePressed, drawing.isPanning, tool]);

  return {
    handleMouseDown,
    getCursorStyle,
  };
}
